import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { Chart, ChartData } from "chart.js";
import { BaseComponent } from "../../../common/baseComponent";
import { DataValueStyle } from "./dataValueStyle";

export class DoughnutChartData {
    public label: string;
    public labelToolTip: string;
    public value: number;

    constructor(label: string, value: number, labelToolTip?: string) {
        this.label = label;
        this.labelToolTip = labelToolTip != null ? labelToolTip : '';
        this.value = value;
    }
}

export class LegendData {
    public color: string;
    public text: string;
    public textToolTip: string;
    public value: number;

    constructor(color: string, text: string, textToolTip: string, value: number) {
        this.color = color;
        this.text = text;
        this.textToolTip = textToolTip;
        this.value = value;
    }
}

export class ClickData {
    public datasetIndices: number[];
    public dataIndices: number[];
    constructor(data: any[]) {
        this.datasetIndices = data.map<number>(x => x._datasetIndex);
        this.dataIndices = data.map<number>(x => x._index);
        if (this.dataIndices.length == 0 || this.datasetIndices.length == 0) throw new Error("Empty data");
    }
}

@Component({
    selector: "doughnut-chart",
    templateUrl: "./doughnutChart.component.html",
    styleUrls: ["./doughnutChart.component.css"],
    standalone: false
})
export class DoughnutChartComponent extends BaseComponent implements AfterViewInit, OnChanges {
    @ViewChild("chartHost", { read: ElementRef }) private chartHost: ElementRef;
    private context: any;
    private chart: any;
    private hostInitialHeight: string;

    public dataValueStyle = DataValueStyle;
    public legends: LegendData[];

    @Input() public data: DoughnutChartData[];
    @Input() public totalStyle: DataValueStyle = DataValueStyle.None;
    @Input() public isVisible: boolean;
    @Input() public isPercentage: boolean = false;
    @Input() public colorPalette: string[] = ["#FFB405", "#00A5ED", "#0F1941", "#6AA4B4"];
    @Output() public clickData: EventEmitter<ClickData> = new EventEmitter<ClickData>();

    constructor(zone: NgZone) {
        super(zone);
    }

    public ngAfterViewInit(): void {
        this.hostInitialHeight = this.chartHost.nativeElement.style.height;
        this.context = this.chartHost.nativeElement.getContext("2d");

        let { data, legends } = this.generateData(this.data);

        setTimeout(() => this.legends = legends, 0);

        let component = this;

        this.chart = new Chart(this.context, {
            type: 'doughnut',
            data: data,
            options: {
                elements: {
                    arc: {
                        borderWidth: 0
                    }
                },
                legend: {
                    display: false
                },
                cutoutPercentage: 80,
                responsive: true,
                maintainAspectRatio: true,
                tooltips: {
                    mode: 'index',
                    intersect: true,
                    // Disable the on-canvas tooltip
                    enabled: false,

                    custom: (tooltip) => {
                        // Tooltip Element
                        var tooltipEl = document.getElementById('chartjs-tooltip');

                        // Create element on first render
                        if (!tooltipEl) {
                            tooltipEl = document.createElement('div');
                            tooltipEl.id = 'chartjs-tooltip';
                            tooltipEl.innerHTML = "<table></table>";
                            document.body.appendChild(tooltipEl);
                        }

                        // Hide if no tooltip
                        if (tooltip.opacity === 0) {
                            tooltipEl.style.opacity = "0";
                            return;
                        }

                        // Set caret Position
                        tooltipEl.classList.remove('above', 'below', 'no-transform');
                        if (tooltip.yAlign) {
                            tooltipEl.classList.add(tooltip.yAlign);
                        } else {
                            tooltipEl.classList.add('no-transform');
                        }

                        function getBody(bodyItem) {
                            return bodyItem.lines;
                        }

                        function getTooltip(body) {
                            return component.isPercentage ? (body[0].split(':').length > 1 ? body[0].split(':')[1] + '%' : '') : body;
                        }

                        // Set Text
                        if (tooltip.body) {
                            var titleLines = tooltip.title || [];
                            var bodyLines = tooltip.body.map(getBody);

                            var innerHtml = '<thead>';

                            titleLines.forEach(function (title) {
                                innerHtml += '<tr><th>' + title + '</th></tr>';
                            });
                            innerHtml += '</thead><tbody>';

                            bodyLines.forEach(function (body, i) {
                                var colors = tooltip.labelColors[i];
                                var style = 'background:' + colors.backgroundColor;
                                style += '; border-color:' + colors.borderColor;
                                style += '; border-width: 2px';
                                var span = '<span class="chartjs-tooltip-key" style="' + style + '"></span>';
                                innerHtml += '<tr><td>' + span + getTooltip(body) + '</td></tr>';
                            });
                            innerHtml += '</tbody>';

                            var tableRoot = tooltipEl.querySelector('table');
                            tableRoot.innerHTML = innerHtml;
                        }

                        let rect = this.chartHost.nativeElement.getBoundingClientRect();
                        var positionX = rect.x;
                        var positionY = rect.y;

                        // Display, position, and set styles for font
                        tooltipEl.style.opacity = "1";
                        tooltipEl.style.left = positionX + tooltip.caretX + 'px';
                        tooltipEl.style.top = positionY + tooltip.caretY + 'px';
                        tooltipEl.style.fontFamily = tooltip._bodyFontFamily;
                        tooltipEl.style.fontSize = `${tooltip.bodyFontSize}px`;
                        tooltipEl.style.fontStyle = tooltip._bodyFontStyle;
                        tooltipEl.style.padding = tooltip.yPadding + 'px ' + tooltip.xPadding + 'px';
                    }
                },
                onClick: (e, arg) => {
                    let tooltip = document.getElementById('chartjs-tooltip');
                    if (tooltip) tooltip.remove();
                    try {
                        this.clickData.emit(new ClickData(arg));
                    } catch {
                    }
                }
            },
            plugins: [{
                beforeDraw: function (chart: any) {
                    let width = chart.chart.width,
                        height = chart.chart.height,
                        ctx = chart.chart.ctx,
                        sizeFontPercentageMin = component.isPercentage ? 54 : 44;

                    ctx.restore();
                    let fontSize = (height / sizeFontPercentageMin).toFixed(2);
                    ctx.font = "100 " + fontSize + "em 'proxima'";
                    ctx.textBaseline = "middle";

                    let text = '';

                    if (component.isPercentage) {
                        ctx.fillStyle = "#E74537";
                        text = Math.max(...chart.config.data.datasets["0"].data).toString() + '%';
                    }
                    else {
                        switch (component.totalStyle) {
                            case DataValueStyle.None: { ctx.fillStyle = "##0E1941"; break; }
                            case DataValueStyle.Zero: { ctx.fillStyle = "#B8D001"; break; }
                            case DataValueStyle.Warn: { ctx.fillStyle = "#EF847B"; break; }
                        }
                        text = chart.config.data.datasets["0"].data.reduce((acc, x) => acc + x, 0).toString();
                    }

                    let textX = Math.round((width - ctx.measureText(text).width) / 2),
                        textY = height / 2;
                    ctx.fillText(text, textX, textY);
                    ctx.save();
                }
            }]
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.chart) {
            let dataChanges = changes["data"];
            if (dataChanges) {
                let { data, legends } = this.generateData(dataChanges.currentValue);
                this.chart.data = data;
                this.chart.update();
                this.legends = legends;
            }

            let isVisibleChanges = changes["isVisible"];
            if (isVisibleChanges && isVisibleChanges.currentValue) {
                this.chartHost.nativeElement.style.height = this.hostInitialHeight;
            }
        }
    }

    private generateData(data: DoughnutChartData[]): { data: ChartData, legends: LegendData[] } {
        if (data) {
            let palette: string[] = [];
            let legends: LegendData[] = [];

            for (let i = 0; i < data.length; ++i) {
                palette.push(this.colorPalette[i % this.colorPalette.length]);
                legends.push(new LegendData(palette[i], data[i].label, data[i].labelToolTip, data[i].value));
            }

            let processedData = {
                labels: data.reduce<string[]>((acc, x) => { acc.push(x.label); return acc; }, []),
                datasets: [{
                    backgroundColor: palette,
                    data: data.reduce<number[]>((acc, x) => { acc.push(x.value); return acc; }, [])
                }]
            };

            return { data: processedData, legends: legends };
        } else {
            return { data: null, legends: [] };
        }
    }

    public legendClick(index: number) {
        this.clickData.emit(new ClickData([{ _datasetIndex: 0, _index: index }]));
    }
}